/**
 * Field allowlists of GET /api/health/dashboard/fleet_battery_health/v2/history/list.
 *
 * Mirrors the backend constants in evcell-v1.1
 * app/database/soh/soh_history_list_db.py:
 *   SOH_HISTORY_GROUP_FIELDS   -> SOH_HISTORY_GROUP_FIELDS  (metaInfo / groupBy / sortBy)
 *   SOH_HISTORY_SELECT_FIELDS  -> SOH_HISTORY_SELECT_FIELDS (select)
 * Any other field makes the endpoint return 400, so keep these in step with the backend.
 */

export const SOH_HISTORY_GROUP_FIELDS = Object.freeze([
  "batteryID",
  "deviceID",
  "fleetID",
  "manufacturingDate",
  "mfgYear",
  "addedOn",
  "batteryState",
  "originCountry",
  "originState",
  "originCity",
  "originDepot",
  "originDepo",
  "licensePlateNo",
  "licensePlateNumber",
  "vin",
  "vehicleSubType",
  "configName",
  "cellChemistry",
  "protocolName",
  "fleetName",
  "fleetCategory",
  "operationalStatus",
  // Current ping status ("Active" / "Inactive"), same meaning as v2/list; drives the
  // online / offline dot in the vehicle cell.
  "pingStatus",
  "sohEst",
  "sohConfidence",
  "eqCycleEst",
  "calendarAgeDaysEst",
  "initialCapacity",
  "sohComputedDate",
  "latestSOHPredictionOdometer",
  // Month value from sohML (v2/list column names for groupBy / metaInfo).
  "sohDegradationRatePerConfiguredTimeRange",
  "sohDegradationRatePerConfiguredCycle",
  "sohDegradationRatePerConfiguredKms",
  "totalEnergyThroughput",
  "totalCapacityThroughput",
  "ratedCapacitySystem",
  "ratedCapacity",
  "ratedEnergy",
  "presentCapacity",
  "presentEnergy",
  "StateOfHealthBucket",
]);

export const SOH_HISTORY_SELECT_FIELDS = Object.freeze([
  "sohEst",
  "sohConfidence",
  "eqCycleEst",
  "calendarAgeDaysEst",
  "initialCapacity",
  "sohComputedDate",
  "latestSOHPredictionOdometer",
  // Month value from sohML (v2/list select names).
  "sohDegradationPerConfiguredTimeRange",
  "sohDegradationPerConfiguredCycle",
  "sohDegradationPerConfiguredKm",
  "totalEnergyThroughput",
  "totalCapacityThroughput",
  "ratedCapacitySystem",
  "ratedCapacity",
  "DistinctVehicleCount",
]);

/** sortBy accepts a select field or a group field. */
export const SOH_HISTORY_SORT_FIELDS = Object.freeze([
  ...new Set([...SOH_HISTORY_SELECT_FIELDS, ...SOH_HISTORY_GROUP_FIELDS]),
]);

// ─── Per level (entityType) ───────────────────────────────────────────────────
// The history list counts units of one level: entityType = "battery" | "pack" | "string".

export const SOH_HISTORY_LEVELS = Object.freeze(["battery", "pack", "string"]);

/** Unit identity fields, valid at every level (groupBy entityID: one row per unit). */
const ENTITY_FIELDS = ["entityID", "entityType"];
/** Pack-level only. */
const PACK_FIELDS = ["packID"];
/** String-level only. */
const STRING_FIELDS = ["stringNo", "stringDisplayID"];
/** Battery-level only: rated / present capacity and energy (400 at pack / string). */
const BATTERY_ONLY_FIELDS = ["ratedCapacitySystem", "ratedCapacity", "ratedEnergy", "presentCapacity", "presentEnergy"];

const levelOf = (level) => (SOH_HISTORY_LEVELS.includes(level) ? level : "battery");

/**
 * The history list's group (metaInfo / groupBy / sortBy), select and sort field sets
 * for one level. Battery-level dimension fields (deviceID, origin*, fleet*, ...) are
 * valid at every level (taken from the unit's battery).
 * @returns {{ group: Set<string>, select: Set<string>, sort: Set<string> }}
 */
export function getSohHistoryFieldSets(level) {
  const lvl = levelOf(level);
  const dropped = new Set(lvl === "battery" ? [] : BATTERY_ONLY_FIELDS);
  const group = new Set([
    ...SOH_HISTORY_GROUP_FIELDS.filter((f) => !dropped.has(f)),
    ...ENTITY_FIELDS,
    ...(lvl === "pack" ? PACK_FIELDS : []),
    ...(lvl === "string" ? STRING_FIELDS : []),
  ]);
  const select = new Set(SOH_HISTORY_SELECT_FIELDS.filter((f) => !dropped.has(f)));
  return { group, select, sort: new Set([...select, ...group]) };
}
